import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabase'
import type { FormDefinition, FormField, Submission } from './types'

export type AppRole = 'user' | 'admin' | 'org_admin'

export interface WorkspaceContext {
  organizationId: string
  organizationName: string
  userId: string
  displayName: string
  email: string
  role: AppRole
}

export interface Category {
  id: string
  name: string
}

export interface LiveForm extends FormDefinition {
  organizationId: string
  categoryId: string | null
  versionId: string | null
  draftFields: FormField[]
}

export interface AccessEntry {
  email: string
  displayName: string
  role: AppRole
  provisioned: boolean
  isCurrentUser: boolean
}

function client() {
  if (!supabase) throw new Error('Supabase is not configured')
  return supabase
}

function fail(error: { message: string } | null, fallback: string) {
  if (error) throw new Error(error.message)
  throw new Error(fallback)
}

export async function loadWorkspace(session: Session): Promise<WorkspaceContext> {
  const db = client()
  const [profileResult, membershipResult] = await Promise.all([
    db.from('profiles').select('id, display_name, email').eq('id', session.user.id).single(),
    db.from('organization_memberships').select('organization_id, role, organizations(name)').eq('user_id', session.user.id).eq('active', true).single(),
  ])
  if (profileResult.error || !profileResult.data) fail(profileResult.error, 'Your employee profile is not provisioned')
  if (membershipResult.error || !membershipResult.data) fail(membershipResult.error, 'You do not have access to this organization')
  const membership = membershipResult.data as unknown as { organization_id: string; role: AppRole; organizations: { name: string } | null }
  const profile = profileResult.data as { id: string; display_name: string; email: string }
  return {
    organizationId: membership.organization_id,
    organizationName: membership.organizations?.name ?? 'Warren Township Police Department',
    userId: profile.id,
    displayName: profile.display_name,
    email: profile.email,
    role: membership.role,
  }
}

export async function loadCategories(organizationId: string): Promise<Category[]> {
  const { data, error } = await client().from('categories').select('id, name').eq('organization_id', organizationId).eq('active', true).order('sort_order')
  if (error) throw new Error(error.message)
  return (data ?? []) as Category[]
}

export async function loadForms(organizationId: string): Promise<LiveForm[]> {
  const db = client()
  const [formsResult, versionsResult] = await Promise.all([
    db.from('forms').select('id, organization_id, category_id, title, description, prefix, status, current_version, icon, draft_schema, categories(name), form_routes(delivery_mode, to_addresses)').eq('organization_id', organizationId).order('updated_at', { ascending: false }),
    db.from('form_versions').select('id, form_id, version, schema').eq('organization_id', organizationId).order('version', { ascending: false }),
  ])
  if (formsResult.error) throw new Error(formsResult.error.message)
  if (versionsResult.error) throw new Error(versionsResult.error.message)
  const versions = (versionsResult.data ?? []) as Array<{ id: string; form_id: string; version: number; schema: { fields?: FormField[] } }>
  return ((formsResult.data ?? []) as unknown as Array<Record<string, unknown>>).map((row) => {
    const currentVersion = versions.find((version) => version.form_id === row.id && version.version === row.current_version)
    const category = row.categories as { name?: string } | null
    const routeValue = row.form_routes as { delivery_mode?: FormDefinition['deliveryMode']; to_addresses?: string[] } | Array<{ delivery_mode?: FormDefinition['deliveryMode']; to_addresses?: string[] }> | null
    const route = Array.isArray(routeValue) ? routeValue[0] : routeValue
    const draftSchema = row.draft_schema as { fields?: FormField[] }
    return {
      id: String(row.id),
      organizationId: String(row.organization_id),
      categoryId: row.category_id ? String(row.category_id) : null,
      versionId: currentVersion?.id ?? null,
      title: String(row.title),
      description: String(row.description ?? ''),
      category: category?.name ?? 'General',
      prefix: String(row.prefix),
      status: row.status as FormDefinition['status'],
      version: Number(row.current_version),
      icon: normalizeIcon(row.icon),
      deliveryMode: route?.delivery_mode ?? 'attachment',
      recipients: route?.to_addresses ?? [],
      fields: currentVersion?.schema?.fields ?? [],
      draftFields: draftSchema?.fields ?? [],
    }
  })
}

export async function createForm(organizationId: string, input: { title: string; description: string; prefix: string; categoryId: string; recipients: string[] }) {
  const { data, error } = await client().rpc('create_form', {
    target_organization: organizationId,
    form_title: input.title,
    form_description: input.description,
    form_prefix: input.prefix,
    form_category: input.categoryId,
    form_icon: 'general',
    recipient_addresses: input.recipients,
  })
  if (error) throw new Error(error.message)
  return data as string
}

export async function saveForm(form: LiveForm) {
  const { error } = await client().rpc('save_form', {
    target_form: form.id,
    form_title: form.title,
    form_description: form.description,
    form_prefix: form.prefix,
    form_category: form.categoryId,
    form_icon: form.icon,
    form_schema: { fields: form.draftFields },
    recipient_addresses: form.recipients,
    form_delivery_mode: form.deliveryMode,
  })
  if (error) throw new Error(error.message)
}

export async function publishForm(form: LiveForm) {
  await saveForm(form)
  const { data, error } = await client().rpc('publish_form', { target_form: form.id })
  if (error) throw new Error(error.message)
  return data as number
}

export async function archiveForm(formId: string) {
  const { error } = await client().from('forms').update({ status: 'archived', updated_at: new Date().toISOString() }).eq('id', formId)
  if (error) throw new Error(error.message)
}

export async function loadSubmissions(organizationId: string): Promise<Submission[]> {
  const { data, error } = await client().from('submissions').select('id, submitter_id, submission_number, status, submitted_at, created_at, forms(title), profiles!submissions_submitter_id_fkey(display_name), email_attempts(status)').eq('organization_id', organizationId).neq('status', 'draft').order('submitted_at', { ascending: false })
  if (error) throw new Error(error.message)
  return ((data ?? []) as unknown as Array<Record<string, unknown>>).map((row) => {
    const form = row.forms as { title?: string } | null
    const profile = row.profiles as { display_name?: string } | null
    const attempts = row.email_attempts as Array<{ status?: string }> | null
    const emailState = attempts?.[0]?.status
    return {
      id: String(row.submission_number ?? row.id),
      submitterId: String(row.submitter_id),
      formTitle: form?.title ?? 'Form',
      submittedAt: String(row.submitted_at ?? row.created_at),
      status: statusLabel(String(row.status)),
      submitter: profile?.display_name ?? 'Employee',
      emailState: emailState === 'sent' || emailState === 'failed' ? emailState : 'pending',
    }
  })
}

export async function submitForm(form: LiveForm, context: WorkspaceContext, values: Record<string, unknown>) {
  if (!form.versionId) throw new Error('This form does not have a published version')
  const db = client()
  const { data: draft, error } = await db.from('submissions').insert({
    organization_id: context.organizationId,
    form_id: form.id,
    form_version_id: form.versionId,
    submitter_id: context.userId,
    values,
  }).select('id').single()
  if (error || !draft) fail(error, 'Unable to create submission')
  const { data, error: functionError } = await db.functions.invoke('submit-form', { body: { submissionId: draft!.id } })
  if (functionError) {
    const response = (functionError as { context?: Response }).context
    let message = functionError.message
    if (response) {
      try {
        const payload = await response.clone().json() as { error?: string }
        if (payload.error) message = payload.error
      } catch {
        // Keep the SDK message when the function did not return JSON.
      }
    }
    throw new Error(message)
  }
  if (data?.error) throw new Error(data.error)
  return data as { submissionId: string; submissionNumber: string }
}

export async function loadAccess(organizationId: string, currentUserId: string): Promise<AccessEntry[]> {
  const db = client()
  const [rulesResult, membersResult] = await Promise.all([
    db.from('access_assignments').select('email, role').eq('organization_id', organizationId).order('email'),
    db.from('organization_memberships').select('user_id, role, profiles(display_name, email)').eq('organization_id', organizationId).eq('active', true),
  ])
  if (rulesResult.error) throw new Error(rulesResult.error.message)
  if (membersResult.error) throw new Error(membersResult.error.message)
  const entries = new Map<string, AccessEntry>()
  for (const row of (rulesResult.data ?? []) as Array<{ email: string; role: AppRole }>) {
    entries.set(row.email, { email: row.email, displayName: row.email, role: row.role, provisioned: false, isCurrentUser: false })
  }
  for (const row of (membersResult.data ?? []) as unknown as Array<{ user_id: string; role: AppRole; profiles: { display_name: string; email: string } | null }>) {
    if (!row.profiles) continue
    entries.set(row.profiles.email, {
      email: row.profiles.email,
      displayName: row.profiles.display_name,
      role: row.role,
      provisioned: true,
      isCurrentUser: row.user_id === currentUserId,
    })
  }
  return [...entries.values()].sort((a, b) => Number(b.isCurrentUser) - Number(a.isCurrentUser) || a.email.localeCompare(b.email))
}

export async function setUserAccess(organizationId: string, email: string, role: AppRole) {
  const { data, error } = await client().rpc('set_user_access', { target_organization: organizationId, target_email: email, target_role: role })
  if (error) throw new Error(error.message)
  return data?.[0] as { email: string; role: AppRole; provisioned: boolean } | undefined
}

function normalizeIcon(value: unknown): FormDefinition['icon'] {
  return value === 'training' || value === 'equipment' || value === 'vehicle' || value === 'it' ? value : 'general'
}

function statusLabel(status: string): Submission['status'] {
  const labels: Record<string, Submission['status']> = { submitted: 'Submitted', under_review: 'Under Review', approved: 'Approved', denied: 'Denied', completed: 'Completed' }
  return labels[status] ?? 'Submitted'
}
